import React, {Fragment} from 'react';
import {useSelector} from 'react-redux';
import moment from 'moment';
import {labels} from '../../../../../../util/utils';
import OutputText from '@ey/canvascoreservices/OutputText';
import MessageAccordion from '../MessageAccordion';
import StyledTimeFrame from './StyledTimeFrame';
import {MessageSend} from '../../../../../../util/uiconstants';

const Activity = ({messagesList, hideMessageLog}) => {
	const userDateFormat = useSelector((store) => store.userPreferences.dateFormat);
	// Messages should be grouped into a common activity component.  Display in reverse chronological order based on date and time,
	// with the newest dates and times at the top of the list.
	// Group messages into the following headings (in this order):

	// "Today"- Date = Current date
	// "Yesterday" - Date = (Current date - 1 day) if the date of the message is the day before the current date.
	// "This week" - Week of the date = Current week (excluding today and yesterday).
	// "Last week" - Week of the date = (Current week - 1 week)
	// "Two weeks ago" - Week of the date = (Current week - 2 weeks)
	// "Older" = All messages older than two weeks
	// Actions should only be displayed under a single heading (i.e. if a date is both yesterday and last week, show it as "Last week" only).

	const todaydate = moment.utc();
	const yesterdaydate = todaydate.clone().subtract(1, 'day');

	const activities = _.orderBy(messagesList.filter(i => i !== null), ['createdDateTime'], ['desc']);

	const [lastWeek, restOfActivities] = _.partition(activities, (activity) =>
		moment(activity.createdDateTime).isSame(moment(todaydate).subtract(1, 'week'), 'week')
	);
	const [today, fromYesterday] = _.partition(restOfActivities, (activity) =>
		moment(activity.createdDateTime).isSame(todaydate, 'day')
	);
	const [yesterday, beforeYesterday] = _.partition(fromYesterday, (activity) =>
		moment(activity.createdDateTime).isSame(yesterdaydate, 'day')
	);
	const [thisWeek, beforeThisWeek] = _.partition(beforeYesterday, (activity) =>
		moment(activity.createdDateTime).isSame(todaydate, 'week')
	);
	const [twoWeeksAgo, older] = _.partition(beforeThisWeek, (activity) =>
		moment(activity.createdDateTime).isSame(moment(todaydate).subtract(2, 'week'), 'week')
	);

	const renderTimeFrame = (title, items) => {
		if (items.length === 0) return null;
		return (
			<StyledTimeFrame>
				{renderTitleWhen(title)}
				{items.map((item, index) => renderActivity(item, index))}
			</StyledTimeFrame>
		);
	};

	const renderTitleWhen = (label) => {
		const textProps = {
			size: 'XL',
			title: label
		};
		return <OutputText {...textProps}>{label}</OutputText>;
	};

	const renderActivity = (item, index) => {
		const isEmail = item.messageType === MessageSend.Email;
		const timeFormat = 'HH:mm';
		const date = moment.utc(item.createdDateTime).local();
		const formDate = date.format(userDateFormat);
		const hour = date.format(timeFormat);

		const props = {
			id: index,
			isEmail: isEmail,
			sentBy: item.sentBy,
			date: formDate,
			time: hour,
			from: item.from,
			subject: item.subject,
			contactsList: item.contacts,
			entitiesList: item.entities,
			countriesList: item.countries,
			groupRolesList: item.groupRoles,
			engagementsList: item.engagements,
			serviceLineList: item.serviceLine,
			rolesList: item.roles,
			fromEngagement: item.fromEngagement,
			message: item.message,
			hideMessageLog
		};

		if (item.link) props.link = {...item.link};

		return (
			<Fragment key={index}>
				<MessageAccordion {...props} />
				<section className="bottom-line" />
			</Fragment>
		);
	};

	const renderAllActivities = () => {
		return (
			<>
				{renderTimeFrame(labels.today, today)}
				{renderTimeFrame(labels.yesterday, yesterday)}
				{renderTimeFrame(labels.thisWeek, thisWeek)}
				{renderTimeFrame(labels.lastWeek, lastWeek)}
				{renderTimeFrame(labels.twoWeeksAgo, twoWeeksAgo)}
				{renderTimeFrame(labels.older, older)}
			</>
		);
	};

	return renderAllActivities();
};

export default Activity;
